import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Image } from 'remix-image';
import { MuiRemixLink } from '~/components/shared/links';
import ShopCarousel, { ShopCarouselSlide } from '~/components/Shop/ShopCarousel';
import data from '~/landings/shopLanding.json';
import { baseImgUrl } from '~/utils/constants';

// Temporary object to display elements in shop
const shopContent = data;

export type ShopContentType = typeof shopContent;
export type ShopContentCardType = {
  title: string | null;
  description?: string | null;
  orientation: string;
  buttonText?: string | null;
  buttonLink: string;
  imageUrl: string;
};
export type ShopContentBannerType = {
  type: string;
  label: string | null;
  title: string;
  description: string;
  imageUrl: string;
};

export default function ShopLanding() {
  function renderShopContent(content: ShopContentType) {
    let elements = content.map((element, index) => {
      switch (element.type) {
        case 'carousel':
          return (
            <ShopCarousel key={`shopCarousel${index}`}>
              {element.slides?.map((slide, index) => (
                <ShopCarouselSlide key={`shopCarouselSlide${index}`} {...slide} imageUrl={{desktop: slide.imageUrl, mobile: slide.imageUrl}} />
              ))}
            </ShopCarousel>
          );

        case 'calloutRow':
          return (
            <ShopCardContainer key={`shopCardContainer${index}`} title={element.title}>
              {element.items?.map((card, index) => {
                const elementsNumber = element?.items?.length;

                return (
                  <ShopCard
                    key={`shopCard${index}`}
                    width={(1 / (elementsNumber || 1)) * 100}
                    elementsNumber={elementsNumber}
                    {...card}
                  />
                );
              })}
            </ShopCardContainer>
          );

        // DISCOUNTS ROW CAROUSEL: inactive due to design decision

        // case 'discountsCalloutRow':
        //   return (
        //     <ShopCarousel
        //       key={`discountCarousel${index}`}
        //       sx={{
        //         overflow: 'visible',
        //         mb: 4,
        //       }}
        //     >
        //       {/* Splits cards by chunks of 3 cards */}
        //       {chunk(
        //         element.items?.map((card: any, index) => (
        //           <CardWithDiscount key={`cardWithDiscount${index}`} width={1 / 3} maxWidth="240px" {...card} />
        //         )),
        //         3
        //         //then return a stack of 3 cards by every chunk
        //       ).map((chunk) => (
        //         <Stack
        //           display="flex"
        //           flexDirection="row"
        //           width="100%"
        //           height="100%"
        //           justifyContent="space-evenly"
        //           borderRadius={1}
        //           bgcolor="#FAFAFA"
        //         >
        //           {chunk}
        //         </Stack>
        //       ))}
        //     </ShopCarousel>
        //   );

        case 'banner':
          return <ShopBanner key={`shopBanner${index}`} {...(element as ShopContentBannerType)} />;

        default:
          return null;
      }
    });

    return elements;
  }

  return (
    <>
      <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0, md: 4 } }}>
        <Stack spacing={4}>{renderShopContent(shopContent)}</Stack>
      </Container>
      <ShopFooter />
    </>
  );
}

export function ShopCardContainer({ children, title }: { children: ReactNode; title?: string | null }) {
  return (
    <Stack px={{ xs: 2, md: 'unset' }}>
      {title && (
        <Typography variant="h2" fontSize={{ xs: 16, md: 24 }} fontWeight={600} marginBottom={{ xs: 0, md: 4 }}>
          {title}
        </Typography>
      )}
      <Stack
        direction="row"
        flexWrap="wrap"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          '@media (max-width:370px)': { justifyContent: 'center' },
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
}

export function ShopCard({
  orientation,
  title,
  imageUrl,
  description,
  buttonText,
  buttonLink,
  width,
  elementsNumber,
}: {
  width: number;
  elementsNumber: number;
} & ShopContentCardType) {
  const isVertical = orientation === 'vertical';

  return (
    <Grid
      item
      display="flex"
      flexDirection={{ xs: 'column', md: isVertical ? 'column' : 'row' }}
      justifyContent="space-between"
      alignItems="center"
      height={{ xs: 'auto', md: !isVertical && elementsNumber === 2 ? 244 : 'auto' }}
      minWidth="160px"
      borderRadius="16px"
      overflow="hidden"
      mt={{ xs: 3, md: 0 }}
      {...(!isVertical ? { p: { xs: 2, md: 4 } } : null)}
      sx={{
        bgcolor: '#FAFAFA',
        width: {
          xs: isVertical && elementsNumber === 2 && !description ? '100%' : `calc(50% - 16px)`,
          md: `calc(${width}% - 16px)`,
        },
        transition: 'box-shadow 300ms',
        transitionDuration: 300,
        '&:hover': { boxShadow: '0px 4px 8px 0px #00000014' },
      }}
    >
      <Box
        width={isVertical ? '100%' : '50%'}
        height={!isVertical ? '100%' : 'auto'}
        display="flex"
        flexDirection="column"
        {...(isVertical ? { p: { xs: elementsNumber === 2 ? '24px 16px 16px' : '16px 16px 16px', md: 3 } } : null)}
        justifyContent="center"
        alignItems={{ xs: 'center', md: !description ? 'center' : 'unset' }}
      >
        <MuiRemixLink href={buttonLink || '/'} sx={{ textDecoration: 'none', width: 'fit-content' }}>
          <Typography
            color="#00598B"
            variant="h3"
            lineHeight={{ md: '41px' }}
            fontSize={{ xs: isVertical && !description && elementsNumber === 2 ? 24 : 14, md: isVertical ? 24 : 34 }}
            fontWeight={600}
            width="fit-content"
          >
            {title}
          </Typography>
        </MuiRemixLink>
        <MuiRemixLink
          href={buttonLink || '/'}
          sx={{ textDecoration: 'none', width: 'fit-content', display: { xs: 'none', md: 'block' } }}
        >
          <Typography color="#00598B" fontSize={16} fontWeight={500} width="fit-content">
            {description}
          </Typography>
        </MuiRemixLink>

        {buttonLink && buttonText && (
          <MuiRemixLink
            href={buttonLink}
            sx={{ margin: 'auto 0 0', textDecoration: 'none', display: { xs: 'none', md: 'block' } }}
          >
            <Button variant="outlined" sx={{ textTransform: 'uppercase', fontSize: '13px' }}>
              {buttonText}
            </Button>
          </MuiRemixLink>
        )}
      </Box>
      <MuiRemixLink sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} href={buttonLink ?? '/'}>
        <Image
          style={{ width: '100%' }}
          src={`${baseImgUrl}${imageUrl}` ?? ''}
          width={214}
          height={214}
          alt="Product"
          options={{ fit: 'inside' }}
          dprVariants={[1, 3]}
          loading="lazy"
        />
      </MuiRemixLink>
    </Grid>
  );
}

// function CardWithDiscount(props: any) {
//   const { description, imageUrl, buttonLink }: ShopContentCardType = props;
//   return (
//     <Box
//       // @ts-ignore: Unreachable code error
//       component={Link}
//       to={buttonLink || '/'}
//       display="flex"
//       flexDirection="column"
//       justifyContent="center"
//       sx={{
//         textDecoration: 'none',
//         '&::before': {
//           content: `'${description}'`,
//           position: 'relative',
//           width: 'fit-content',
//           height: 'auto',
//           bgcolor: '#F59126',
//           px: 4,
//           py: 1,
//           fontSize: '20px',
//           fontWeight: 500,
//           color: 'white',
//           inset: 0,
//           top: '40px',
//           margin: '0 auto',
//         },
//       }}
//       {...props}
//     >
//       <Image src={imageUrl} />
//     </Box>
//   );
// }

function ShopBanner({ imageUrl, title, description, label }: ShopContentBannerType) {
  return (
    <>
      {label && (
        <Typography variant="h2" fontSize={{ xs: 16, md: 24 }} fontWeight={600} marginBottom={{ xs: 0, md: 4 }}>
          {label}
        </Typography>
      )}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        p={{ xs: 3, md: 8 }}
        bgcolor="#FAFAFA"
        width="100%"
        height="484px"
        borderRadius="8px"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack width={{ xs: '90%', md: '50%' }}>
          <Typography
            textAlign={{ xs: 'center', md: 'left' }}
            color="#00598B"
            fontWeight={{ xs: 600, md: 700 }}
            fontSize={{ xs: 30, md: 60 }}
            mb={{ xs: 3, md: 6 }}
            lineHeight={{ xs: '32px', md: '72px' }}
          >
            {title}
          </Typography>
          <Typography
            textAlign={{ xs: 'center', md: 'left' }}
            color="#00598B"
            fontWeight={500}
            fontSize={{ xs: 16, md: 20 }}
          >
            {description}
          </Typography>
        </Stack>
        <Image src={`${baseImgUrl}${imageUrl}`} />
      </Box>
    </>
  );
}

function ShopFooter() {
  return (
    <Stack
      mt={20}
      pt={10}
      sx={{
        bgcolor: '#F2E8E3',
        height: '360px',
        width: { xs: '100vw', sm: 'calc(100vw - 20px)' },
        marginLeft: { xs: 'calc(-50vw + 50%)', sm: 'calc(-50vw + 50% + 10px)' },
        marginBottom: '-80px',
      }}
      alignItems="center"
    >
      <Typography
        variant="h3"
        fontSize={{ xs: 20, md: 34 }}
        fontWeight={600}
        lineHeight={{ xs: '32px', md: '42px' }}
        textAlign="center"
        width={{ xs: '75%', md: '100%' }}
      >
        Can't find your package? <hr style={{ visibility: 'hidden', margin: '0' }} />
        Do you need help with your design?
      </Typography>
      <Typography fontSize={16} fontWeight={400} textAlign="center" mt={3}>
        We have the best designers at your disposal
      </Typography>
      <a href="mailto:support@swagcharm.com" style={{ textDecoration: 'none', marginTop: '24px' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Box
            sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, bgcolor: '#302F54', borderRadius: 2 }}
          />
          <Button
            variant="contained"
            size="large"
            sx={{
              px: 2,
              width: '100%',
              textTransform: 'uppercase',
              transform: 'translate(4px, -4px)',
              boxShadow: 'none !important',
              borderRadius: 2,
              transition: 'transform 250ms ease',
              '&:focus-visible, &:active': {
                transform: 'translate(2px, -2px)',
              },
            }}
          >
            Contact us
          </Button>
        </Box>
      </a>
    </Stack>
  );
}
